import { components, createFilter, GroupBase, OptionProps } from 'react-select';
import { FilterOptionOption } from 'react-select/dist/declarations/src/filters';

import { PortainerSelect, Option } from '@@/form-components/PortainerSelect';

import { ChartVersion } from '../helmChartSourceQueries/useHelmRepoVersions';

const filterConfig = {
  // stringify the option to make the machine labels searchable
  stringify: (version: FilterOptionOption<Option<ChartVersion | null>>) => {
    if (!version.data) {
      return '';
    }

    return `${version.data.value?.version} ${version.data.value?.appVersion}`;
  },
};

/**
 * Select component for Helm chart versions, using PortainerSelect.
 * Supports nullable ChartVersion value.
 */
export interface HelmVersionSelectProps {
  value: ChartVersion | null;
  options: Option<ChartVersion>[];
  onChange: (version: ChartVersion | null) => void;
  isLoading?: boolean;
  disabled?: boolean;
  'data-cy': string;
  isClearable?: boolean;
  isChartSelected?: boolean;
}

export function HelmVersionSelect({
  value,
  options,
  onChange,
  isLoading,
  disabled,
  isClearable,
  isChartSelected,
  'data-cy': dataCy,
}: HelmVersionSelectProps) {
  const selectedOption = value
    ? options.find((opt) => opt.value.version === value.version) || null
    : null;

  return (
    <PortainerSelect<ChartVersion | null>
      value={selectedOption?.value || null}
      options={options}
      onChange={(option) => onChange(option ?? null)}
      isClearable={isClearable}
      data-cy={dataCy}
      disabled={disabled}
      placeholder={getVersionSelectPlaceholder()}
      isLoading={isLoading}
      noOptionsMessage={() => 'No versions available.'}
      loadingMessage={() => 'Loading versions...'}
      components={{
        Option: ChartOption,
      }}
      filterOption={createFilter(filterConfig)}
    />
  );

  function getVersionSelectPlaceholder() {
    if (isLoading) {
      return 'Loading versions...';
    }

    if (!isChartSelected) {
      return 'No versions available';
    }
    return 'Select a version...';
  }
}

function ChartOption(
  props: OptionProps<
    Option<ChartVersion | null>,
    false,
    GroupBase<Option<ChartVersion | null>>
  >
) {
  const { data: option } = props;
  const { label, value } = option || {};

  if (!label || !value) {
    return null;
  }

  return (
    <div>
      {/* eslint-disable-next-line react/jsx-props-no-spreading */}
      <components.Option {...props} className="truncate whitespace-nowrap">
        <div
          className="text-ellipsis whitespace-nowrap font-medium text-inherit"
          title={label}
        >
          {label}
        </div>
        {!!value.appVersion && (
          <div className="text-muted mt-1 overflow-hidden text-ellipsis whitespace-normal text-xs">
            App version: {value.appVersion}
          </div>
        )}
      </components.Option>
    </div>
  );
}
